import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { generateAITabletopScenarios, api } from '../../services/api';
import { useSubscriptionContext } from '../../contexts/SubscriptionContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTabletopHeaderActions } from '../../contexts/TabletopHeaderActionsContext';
import paymentService from '../../services/paymentService';
import { usePartnerTokenTiers } from '../../contexts/PartnerTokenTiersContext';

interface Exercise {
  id: number;
  exercise_name: string;
  exercise_type: string;
  status: string;
  scheduled_date: string | null;
  completed_date: string | null;
  admin_approved: boolean;
  iso_auditor_approved: boolean;
  document_download_enabled: boolean;
  uploaded_document_url?: string | null;
  audit_requested?: boolean;
  bcp_id: number;
  partner_token_tier?: 'simple' | 'gold' | 'premium' | null;
}

interface ExerciseDashboardProps {
  bcpId?: number;
  hidePageHeader?: boolean;
}

const STEPS = ['Set up', 'Approved', 'Run', 'Results uploaded', 'ISO assessment'] as const;

const ExerciseDashboard: React.FC<ExerciseDashboardProps> = ({ bcpId, hidePageHeader = false }) => {
  const { user } = useAuth();
  const { tokenTitles, tokenLetters } = usePartnerTokenTiers();
  const isPartnerClient = Boolean(user?.insurer_org_id && user?.role === 'USER');
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [credits, setCredits] = useState<number>(0);
  const [creditsPerPack, setCreditsPerPack] = useState<number>(1);
  const [creditsLoaded, setCreditsLoaded] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [partnerOrgName, setPartnerOrgName] = useState<string | null>(null);
  const [entitlementAllowed, setEntitlementAllowed] = useState(false);
  const [reschedulingExercise, setReschedulingExercise] = useState<Exercise | null>(null);
  const [newScheduledDate, setNewScheduledDate] = useState('');
  const [isSavingDate, setIsSavingDate] = useState(false);

  const { canAccessFeature } = useSubscriptionContext();
  const { setActions } = useTabletopHeaderActions();

  const includedBySubscription = canAccessFeature('tabletop_scenarios');

  const packPurchaseLabel =
    creditsPerPack <= 1 ? 'Buy tabletop credit — $500' : `Buy ${creditsPerPack}-Pack — $500`;

  const fetchExercises = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/v1/tabletop-exercises/');
      setExercises(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Error fetching exercises');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchCredits = useCallback(async () => {
    const entitlementRes = await api.get('/v1/tabletop-exercises/entitlement/').catch(() => null);
    const entitlement = entitlementRes?.data;
    const access = entitlement?.access || entitlement?.source;

    if (access?.kind === 'partner_credit') {
      setPartnerOrgName(access.insurer_name || 'Your insurance partner');
    } else {
      setPartnerOrgName(null);
    }
    setEntitlementAllowed(Boolean(entitlement?.allowed));

    try {
      const data = await paymentService.getStandaloneTabletopCredits();
      setCredits(data.credits_remaining || 0);
      setCreditsPerPack(data.credits_per_pack ?? 1);
    } catch {
      const fallbackCredits =
        typeof entitlement?.credits_remaining === 'number' ? entitlement.credits_remaining : 0;
      setCredits(fallbackCredits);
      setCreditsPerPack(1);
    } finally {
      setCreditsLoaded(true);
    }
  }, []);

  const handleBuyCredits = useCallback(async () => {
    setIsPurchasing(true);
    setError(null);
    try {
      const fresh = await paymentService.getStandaloneTabletopCredits().catch(() => null);
      const freshBalance = fresh?.credits_remaining ?? credits;
      if (freshBalance !== credits) setCredits(freshBalance);
      const pack = fresh?.credits_per_pack ?? creditsPerPack;
      if (freshBalance >= pack) {
        setError(`You already have ${freshBalance} unused tabletop credits. Use some before buying more.`);
        setIsPurchasing(false);
        return;
      }
      const data = await paymentService.createStandaloneTabletopCheckoutSession({
        returnTo: '/tabletop-exercises',
      });
      window.location.href = data.checkout_url;
    } catch (err: any) {
      setError(err?.message || 'Could not start checkout');
      setIsPurchasing(false);
    }
  }, [credits, creditsPerPack]);

  useEffect(() => {
    fetchExercises();
    fetchCredits();
  }, [bcpId, fetchExercises, fetchCredits]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const sessionId = url.searchParams.get('purchase_session_id');
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        await paymentService.getCheckoutSession(sessionId);
      } catch {
        // best-effort
      }
      if (cancelled) return;
      await fetchCredits();
      url.searchParams.delete('purchase_session_id');
      window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchCredits]);

  const handleGenerateAIScenarios = useCallback(async () => {
    if (!bcpId) {
      setError('BCP ID is required for AI generation');
      return;
    }

    setIsGeneratingAI(true);
    try {
      const response = await generateAITabletopScenarios(bcpId, 3);
      if (response.success && response.scenarios) {
        await fetchExercises();
      } else {
        setError('Failed to generate AI scenarios');
      }
    } catch (error) {
      setError('Failed to generate AI scenarios');
    } finally {
      setIsGeneratingAI(false);
    }
  }, [bcpId, fetchExercises]);

  const handleSaveReschedule = async () => {
    if (!reschedulingExercise || !newScheduledDate) return;
    setIsSavingDate(true);
    try {
      await api.put(`/v1/tabletop-exercises/${reschedulingExercise.id}`, {
        scheduled_date: new Date(newScheduledDate).toISOString(),
      });
      setReschedulingExercise(null);
      setNewScheduledDate('');
      await fetchExercises();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to reschedule exercise');
    } finally {
      setIsSavingDate(false);
    }
  };

  const canCreate = includedBySubscription || credits > 0 || entitlementAllowed;
  const partnerWaitingForCredit =
    isPartnerClient && creditsLoaded && credits === 0 && !includedBySubscription && !entitlementAllowed;
  const partnerCreditUsedUp =
    isPartnerClient &&
    creditsLoaded &&
    credits === 0 &&
    exercises.length > 0 &&
    !includedBySubscription &&
    !entitlementAllowed;

  useEffect(() => {
    if (!hidePageHeader) {
      setActions(null);
      return () => setActions(null);
    }

    if (canCreate) {
      setActions(
        <Link
          to="/tabletop-exercises/new"
          className="px-3.5 py-1.5 bg-[#991B1B] hover:bg-[#7F1D1D] text-white rounded-lg text-xs font-bold shadow-sm transition-colors flex items-center space-x-1"
        >
          <span>+</span>
          <span>New exercise</span>
        </Link>
      );
    } else if (partnerWaitingForCredit) {
      setActions(
        <span className="text-xs text-slate-600 font-medium">
          Waiting for credit from {partnerOrgName || 'your insurer'}
        </span>
      );
    } else if (partnerCreditUsedUp) {
      setActions(
        <span className="text-xs text-slate-600 font-medium">
          Exercise complete — contact {partnerOrgName || 'your insurer'} for another
        </span>
      );
    } else if (exercises.length > 0) {
      setActions(
        <button
          onClick={handleBuyCredits}
          disabled={isPurchasing}
          className="px-3.5 py-1.5 bg-[#991B1B] hover:bg-[#7F1D1D] text-white rounded-lg text-xs font-bold shadow-sm transition-colors disabled:opacity-50 flex items-center space-x-1"
        >
          <span>{isPurchasing ? 'Redirecting…' : packPurchaseLabel}</span>
        </button>
      );
    } else {
      setActions(null);
    }

    return () => setActions(null);
  }, [
    hidePageHeader,
    canCreate,
    partnerWaitingForCredit,
    partnerCreditUsedUp,
    exercises.length,
    handleBuyCredits,
    isPurchasing,
    packPurchaseLabel,
    partnerOrgName,
    setActions,
  ]);

  const isDatePassed = (exercise: Exercise) => {
    if (!exercise.scheduled_date) return false;
    try {
      const scheduled = new Date(exercise.scheduled_date);
      return scheduled.getTime() < Date.now() && exercise.status !== 'completed';
    } catch {
      return false;
    }
  };

  const formatScheduledDate = (dateStr: string | null) => {
    if (!dateStr) return 'Not scheduled';
    try {
      const d = new Date(dateStr);
      return `Scheduled ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } catch {
      return 'Not scheduled';
    }
  };

  const getStatusDisplay = (exercise: Exercise) => {
    if (exercise.status === 'draft') {
      return {
        text: 'Draft',
        pillClass: 'bg-slate-100 text-slate-700 border border-slate-200',
      };
    }
    if (exercise.status === 'completed' || (exercise.admin_approved && exercise.iso_auditor_approved)) {
      return {
        text: 'Completed',
        pillClass: 'bg-emerald-50 text-emerald-800 border border-emerald-200',
      };
    }
    if (exercise.audit_requested || (exercise.admin_approved && !exercise.iso_auditor_approved && exercise.uploaded_document_url)) {
      return {
        text: 'In ISO assessment',
        pillClass: 'bg-blue-50 text-blue-700 border border-blue-200',
      };
    }
    if (!exercise.admin_approved) {
      return {
        text: 'Waiting for approval',
        pillClass: 'bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]',
      };
    }
    return {
      text: 'Approved',
      pillClass: 'bg-emerald-50 text-emerald-800 border border-emerald-200',
    };
  };

  const getActiveStepIndex = (exercise: Exercise) => {
    if (exercise.status === 'draft') return 0; // 'Set up' in progress
    if (!exercise.admin_approved) return 1; // 'Approved' in progress
    if (!exercise.uploaded_document_url && !exercise.audit_requested && exercise.status !== 'completed') {
      return 2; // 'Run' in progress
    }
    if (!exercise.audit_requested && !exercise.iso_auditor_approved && exercise.status !== 'completed') {
      return 3; // 'Results uploaded' in progress
    }
    if (exercise.audit_requested && !exercise.iso_auditor_approved && exercise.status !== 'completed') {
      return 4; // 'ISO assessment' in progress
    }
    return -1; // All completed
  };

  const getContextualText = (exercise: Exercise) => {
    if (isDatePassed(exercise)) {
      return "Pick a new date before it's approved — the scheduled date has passed.";
    }
    const status = getStatusDisplay(exercise).text;
    if (status === 'In ISO assessment') {
      return 'Results submitted. An ISO 22301 assessor is reviewing them.';
    }
    if (exercise.status === 'draft') {
      return 'Continue configuring your exercise parameters.';
    }
    if (!exercise.admin_approved) {
      return 'Exercise submitted. Waiting for admin approval.';
    }
    if (exercise.status === 'completed' || exercise.iso_auditor_approved) {
      return 'Crisis rehearsal and assessment sign-off completed.';
    }
    return 'Exercise approved and ready to run.';
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="h-4 bg-slate-200 rounded w-1/3"></div>
          <div className="space-y-4 mt-8">
            <div className="h-40 bg-slate-200 rounded-xl"></div>
            <div className="h-40 bg-slate-200 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header section matching Image 1 */}
      {!hidePageHeader && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0F172A] tracking-tight">
              Tabletop exercises
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Plan, run and submit crisis rehearsals for your team.
            </p>
          </div>

          <div className="flex items-center gap-3">
            {bcpId && includedBySubscription && (
              <button
                onClick={handleGenerateAIScenarios}
                disabled={isGeneratingAI}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-colors disabled:opacity-50 text-sm font-medium flex items-center shadow-xs"
              >
                {isGeneratingAI ? (
                  <>
                    <div className="mr-2 w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Generating AI Scenarios...
                  </>
                ) : (
                  <>
                    <span className="mr-1.5">🤖</span>
                    Generate AI Scenarios
                  </>
                )}
              </button>
            )}

            {canCreate ? (
              <Link
                to="/tabletop-exercises/new"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#991B1B] hover:bg-[#7F1D1D] text-white font-medium rounded-lg text-sm shadow-xs transition-colors"
              >
                <span className="text-base font-normal leading-none">+</span>
                <span>New exercise</span>
              </Link>
            ) : partnerWaitingForCredit ? (
              <span className="text-sm text-slate-600">Waiting for credit from {partnerOrgName || 'your insurer'}</span>
            ) : partnerCreditUsedUp ? (
              <span className="text-sm text-slate-600">Exercise complete — contact {partnerOrgName || 'your insurer'} for another</span>
            ) : exercises.length > 0 ? (
              <button
                onClick={handleBuyCredits}
                disabled={isPurchasing}
                className="inline-flex items-center px-4 py-2 bg-[#991B1B] hover:bg-[#7F1D1D] text-white font-medium rounded-lg text-sm shadow-xs transition-colors disabled:opacity-50"
              >
                {isPurchasing ? 'Redirecting…' : packPurchaseLabel}
              </button>
            ) : null}
          </div>
        </div>
      )}

      {/* Waiting for credit banner matching Image 1 */}
      {partnerWaitingForCredit && (
        <div className="bg-[#FEF3C7]/80 border border-[#FDE68A] rounded-xl p-4 sm:p-5 flex items-start justify-between gap-4 shadow-2xs">
          <div className="flex items-start gap-3.5">
            <div className="w-10 h-10 rounded-full bg-white border border-[#FDE68A] flex items-center justify-center shrink-0 text-[#92400E] shadow-2xs">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-[#92400E]">
                Waiting for credit from {partnerOrgName || 'your insurer'}
              </h4>
              <p className="text-xs sm:text-sm text-[#92400E]/90 mt-0.5">
                Your exercises are saved. They'll be approved and the worksheets unlocked as soon as your insurer adds credit to your account.
              </p>
            </div>
          </div>
          <a
            href="mailto:support@flashemergencymanagement.com"
            className="text-xs sm:text-sm font-semibold text-[#92400E] hover:underline whitespace-nowrap pt-1"
          >
            Contact support
          </a>
        </div>
      )}

      {partnerOrgName && credits > 0 && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-900 shadow-2xs">
          Covered by {partnerOrgName} — {credits} credit{credits === 1 ? '' : 's'} remaining
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800 shadow-2xs flex items-center justify-between">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="text-xs text-red-600 underline">Dismiss</button>
        </div>
      )}

      {/* Exercises list */}
      {exercises.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200 shadow-xs p-6">
          <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">No exercises yet</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
            {canCreate
              ? 'Create your first table top exercise — works with or without an existing BCP.'
              : partnerWaitingForCredit
              ? `${partnerOrgName || 'Your insurance partner'} will provision your tabletop exercise. You'll receive an email when it's ready — check back here to create your exercise.`
              : partnerCreditUsedUp
              ? `You've used your provisioned exercise. Contact ${partnerOrgName || 'your insurance partner'} if you need to run another tabletop.`
              : `$500 buys ${creditsPerPack === 1 ? 'one AI-generated tabletop exercise' : `${creditsPerPack} AI-generated tabletop exercises`}. No BCP required.`}
          </p>
          {canCreate ? (
            <Link
              to="/tabletop-exercises/new"
              className="inline-flex items-center px-4 py-2 bg-[#991B1B] hover:bg-[#7F1D1D] text-white font-medium rounded-lg text-sm shadow-xs transition-colors"
            >
              Create Exercise
            </Link>
          ) : partnerWaitingForCredit || partnerCreditUsedUp ? null : (
            <button
              onClick={handleBuyCredits}
              disabled={isPurchasing}
              className="inline-flex items-center px-4 py-2 bg-[#991B1B] hover:bg-[#7F1D1D] text-white font-medium rounded-lg text-sm shadow-xs transition-colors disabled:opacity-50"
            >
              {isPurchasing ? 'Redirecting…' : packPurchaseLabel}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {exercises.map((exercise) => {
            const statusDisplay = getStatusDisplay(exercise);
            const activeStep = getActiveStepIndex(exercise);
            const datePassed = isDatePassed(exercise);

            return (
              <div
                key={exercise.id}
                className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 shadow-xs hover:border-slate-300 transition-all space-y-4"
              >
                {/* Header row: Title and Status pill */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <h3 className="text-lg font-bold text-slate-900 tracking-tight">
                    {exercise.exercise_name}
                  </h3>
                  <div>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${statusDisplay.pillClass}`}>
                      {statusDisplay.text}
                    </span>
                  </div>
                </div>

                {/* Badges row: category pill, scheduled date, date passed */}
                <div className="flex items-center gap-2.5 flex-wrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 capitalize">
                    {exercise.exercise_type}
                  </span>
                  {exercise.partner_token_tier && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                      <span className="font-bold">{tokenLetters[exercise.partner_token_tier] || 'T'}</span>
                      <span>·</span>
                      <span>{tokenTitles[exercise.partner_token_tier] || exercise.partner_token_tier}</span>
                    </span>
                  )}
                  <span className="text-xs font-medium text-slate-500">
                    {formatScheduledDate(exercise.scheduled_date)}
                  </span>
                  {datePassed && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                      Date has passed
                    </span>
                  )}
                </div>

                {/* 5-Step Segmented Progress Stepper matching Image 1 */}
                <div className="grid grid-cols-5 gap-2 sm:gap-4 pt-2">
                  {STEPS.map((step, idx) => {
                    const isCompleted = activeStep === -1 || idx < activeStep;
                    const isActive = idx === activeStep;

                    const barColor = isCompleted
                      ? 'bg-[#15803D]'
                      : isActive
                      ? 'bg-[#C2410C]'
                      : 'bg-slate-200';

                    const textColor = isActive
                      ? 'font-bold text-slate-900'
                      : isCompleted
                      ? 'font-medium text-slate-700'
                      : 'font-normal text-slate-400';

                    return (
                      <div key={step} className="flex flex-col gap-1.5">
                        <div className={`h-1.5 rounded-full w-full ${barColor}`} />
                        <span className={`text-[11px] sm:text-xs truncate ${textColor}`}>
                          {step}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Footer contextual message and actions matching Image 1 */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t border-slate-100">
                  <p className="text-xs sm:text-sm text-slate-600">
                    {getContextualText(exercise)}
                  </p>

                  <div className="flex items-center gap-2 shrink-0">
                    {datePassed && (
                      <button
                        type="button"
                        onClick={() => {
                          setReschedulingExercise(exercise);
                          if (exercise.scheduled_date) {
                            try {
                              setNewScheduledDate(new Date(exercise.scheduled_date).toISOString().slice(0, 16));
                            } catch {
                              setNewScheduledDate('');
                            }
                          }
                        }}
                        className="px-3.5 py-1.5 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded-lg shadow-2xs transition-colors"
                      >
                        Reschedule
                      </button>
                    )}

                    {exercise.status === 'draft' && (
                      <Link
                        to={`/tabletop-exercises/${exercise.id}/edit`}
                        className="px-3.5 py-1.5 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded-lg shadow-2xs transition-colors"
                      >
                        Edit
                      </Link>
                    )}

                    <Link
                      to={`/tabletop-exercises/${exercise.id}`}
                      className="px-4 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white text-xs font-semibold rounded-lg shadow-2xs transition-colors"
                    >
                      View exercise
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reschedule Modal */}
      {reschedulingExercise && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">Reschedule exercise</h3>
              <button
                type="button"
                onClick={() => setReschedulingExercise(null)}
                className="text-slate-400 hover:text-slate-600 text-lg font-bold"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-slate-600">
              Select a new scheduled date and time for{' '}
              <span className="font-semibold text-slate-900">{reschedulingExercise.exercise_name}</span>.
            </p>
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">
                New scheduled date & time
              </label>
              <input
                type="datetime-local"
                value={newScheduledDate}
                min={new Date().toISOString().slice(0, 16)}
                onChange={(e) => setNewScheduledDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#991B1B]"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setReschedulingExercise(null)}
                className="px-4 py-2 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!newScheduledDate || isSavingDate}
                onClick={handleSaveReschedule}
                className="px-4 py-2 text-xs font-semibold rounded-lg bg-[#991B1B] hover:bg-[#7F1D1D] text-white transition-colors disabled:opacity-50"
              >
                {isSavingDate ? 'Saving…' : 'Save date'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExerciseDashboard;
